import {
  FlexComponentMap,
  FlexComponentMapOptions
} from "components/flexFramework/FlexComponents/typings";
import { Logger } from "bernie-logger";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { BlossomComponent } from "../domain/BlossomComponent";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Localization } from "bernie-l10n";
import { ReactElement } from "react";

/**
 * Base class that knows how to render a bunch of Blossom components given a Flex Component Map.
 * This knows how to render a list of Blossom Components as well as if they should be included or not.
 *
 * In addition, it knows how to code split appropriately.
 *
 * TODO: There are some components such as TabGroup or CTACarouselRegion who should make a new implementation of this.
 */
export abstract class BlossomRenderer {
  private readonly componentMap: FlexComponentMap;
  private readonly components: BlossomComponent[];

  protected constructor(componentMap: FlexComponentMap, components: BlossomComponent[]) {
    this.componentMap = componentMap;
    this.components = components;
  }

  public render = (logger: Logger, l10n: Localization, context: ExtendedContextStore) => {
    const keyHelper = new ItemKeyHelper("flexComponent");

    return this.components
      .map(component =>
        component.render(
          this.componentMap,
          component.getId() || keyHelper.next(),
          context,
          l10n,
          logger
        )
      )
      .filter(component => component) as ReactElement[];
  };

  /**
   * Returns a list of webpack react components from the Flex Component Map that we plan to use.
   * If no components are present, then we return all the values from the Flex Component Map.
   */
  public getWebpackReactComponents = () => {
    // Return all components for server-side
    if (!this.components || this.components.length === 0) {
      return [...this.componentMap.values()];
    }

    const reducer = (acc: Set<FlexComponentMapOptions>, component: BlossomComponent) => {
      if (!component || component.name === undefined) {
        return acc;
      }

      const reactComponent = component.getReactComponent(this.componentMap);
      if (reactComponent) {
        acc.add(reactComponent);
      }

      if (component.getBlossomComponents()) {
        component.getBlossomComponents().forEach(module => {
          reducer(acc, module);
        });
      }

      return acc;
    };

    return [
      ...this.components
        .filter(component => component.shouldRender())
        .reduce(reducer, new Set<FlexComponentMapOptions>())
    ];
  };

  /**
   * Uses getWebpackReactComponents() to get webpack react components and loads them.
   */
  public codeSplitAllReactComponents = () => {
    const componentsToCodeSplit = this.getWebpackReactComponents()
      .filter(
        module => module && module.codeSplit && typeof module.codeSplit.loadRoute === "function"
      )
      .map(module => module && module.codeSplit.loadRoute());

    return Promise.all(componentsToCodeSplit);
  };
}
